import { FuelConverter } from "../utils/FuelUtils";
import { Aircraft, AircraftWbLatPoint, AircraftWbLonPoint, AircraftWbStation, getWbMaxMass } from "./aircraft";
import Flight, { FlightFuelOnBoard, FlightWbLoad } from "./flight";
import { isPassengerOnLeg } from "./flightLegs";

//A calculated loading point. arm/latArm are rounded for display; moment/latMoment are the exact
//sums (mass × arm) so later phases and moment columns never compound display rounding.
export type WbPoint = AircraftWbLonPoint & AircraftWbLatPoint & { moment: number; latMoment: number };

type Mass = { mass: number; moment: number; latMoment: number };

function toPoint(m: Mass): WbPoint {
  const arm = m.mass > 0 ? m.moment / m.mass : 0
  const latArm = m.mass > 0 ? m.latMoment / m.mass : 0
  return { mass: m.mass, arm: Math.round(arm), latArm: Math.round(latArm), moment: m.moment, latMoment: m.latMoment }
}

function addFuel(base: Mass, fuelOnBoard: FlightFuelOnBoard[] | undefined): Mass {
  return (fuelOnBoard ?? []).reduce((acc, f) => ({
    mass: acc.mass + f.mass,
    moment: acc.moment + f.mass * f.station.arm,
    latMoment: acc.latMoment + f.mass * (f.station.latArm ?? 0),
  }), base)
}

function calculateEmptyFuelMass(aircraft: Aircraft, wbLoads: FlightWbLoad[]): Mass {
  const empty = aircraft.wb!.emptyMass
  return wbLoads.reduce((acc, l) => ({
    mass: acc.mass + l.mass,
    moment: acc.moment + l.mass * l.station.arm,
    latMoment: acc.latMoment + l.mass * (l.station.latArm ?? 0),
  }), { mass: empty.mass, moment: empty.mass * empty.arm, latMoment: empty.mass * (empty.latArm ?? 0) })
}

function burnFuel(fuelOnBoard: FlightFuelOnBoard[], fuelMassToBurn: number): FlightFuelOnBoard[] {

  var totalFuelMass = fuelOnBoard.reduce((total, tank) => total + tank.mass, 0);
  const newFuelOnBoard: FlightFuelOnBoard[] = JSON.parse(JSON.stringify(fuelOnBoard))

  while (fuelMassToBurn > 0 && totalFuelMass > 0) {
    const remainingTanks = newFuelOnBoard.filter(tank => tank.mass > 0);
    const burnRatePerTank = fuelMassToBurn / remainingTanks.length;

    for (let i = 0; i < remainingTanks.length; i++) {
      const tank = remainingTanks[i];
      const fuelToBurnFromTank = Math.min(tank.mass, burnRatePerTank);
      const newMass = tank.mass - fuelToBurnFromTank;

      tank.mass = Math.round(newMass);

      fuelMassToBurn -= fuelToBurnFromTank;
      totalFuelMass -= fuelToBurnFromTank;

      if (fuelMassToBurn <= 0 || totalFuelMass <= 0) {
        break;
      }
    }
  }

  return newFuelOnBoard;
}

// ─── envelope geometry ───────────────────────────────────────────────────────

type Point = {
  x: number;
  y: number;
};

const EDGE_EPSILON = 1e-6

function isOnSegment(p1: Point, p2: Point, x: number, y: number): boolean {
  const cross = (x - p1.x) * (p2.y - p1.y) - (y - p1.y) * (p2.x - p1.x)
  const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
  if (Math.abs(cross) / len > EDGE_EPSILON) return false
  return x >= Math.min(p1.x, p2.x) - EDGE_EPSILON && x <= Math.max(p1.x, p2.x) + EDGE_EPSILON
    && y >= Math.min(p1.y, p2.y) - EDGE_EPSILON && y <= Math.max(p1.y, p2.y) + EDGE_EPSILON
}

//Ray casting; a point on the boundary counts as inside (the limit itself is allowed).
function isInsideEnvelope(points: Point[], x: number, y: number): boolean {
  var inside = false
  for (var i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i], b = points[j]
    if (isOnSegment(a, b, x, y)) return true
    if ((a.y > y) !== (b.y > y) && x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x) {
      inside = !inside
    }
  }
  return inside
}

//Which limit an outside point breaks, for the error texts: y (mass / lateral arm) when the vertical
//section of the envelope at x exists but the point is above or below it, otherwise x (CG arm).
//Exactly one of the two is true for an outside point, both false inside.
function checkIsOutOfLimits(points: Point[], x: number, y: number): { x: boolean, y: boolean } {
  if (points.length < 3 || isInsideEnvelope(points, x, y)) {
    return { x: false, y: false }
  }
  const ys: number[] = []
  points.forEach((p1, i) => {
    const p2 = points[(i + 1) % points.length]
    if (x < Math.min(p1.x, p2.x) || x > Math.max(p1.x, p2.x)) return
    if (p1.x === p2.x) {
      ys.push(p1.y, p2.y)
    } else {
      ys.push(p1.y + (x - p1.x) * (p2.y - p1.y) / (p2.x - p1.x))
    }
  })
  if (ys.length === 0) {
    return { x: true, y: false }
  }
  if (y > Math.max(...ys) || y < Math.min(...ys)) {
    return { x: false, y: true }
  }
  return { x: true, y: false }
}

//Exact arms from the moments when the point carries them; plain envelope points only have arm.
function exactArm(p: AircraftWbLonPoint & Partial<WbPoint>): number {
  return p.moment !== undefined && p.mass > 0 ? p.moment / p.mass : p.arm
}
function exactLatArm(p: AircraftWbLatPoint & Partial<WbPoint>): number {
  return p.latMoment !== undefined && p.mass !== undefined && p.mass > 0 ? p.latMoment / p.mass : p.latArm
}

export function hasLatEnvelopePoints(aircraft: Aircraft): boolean {
  return (aircraft.wb?.latEnvelopePoints?.length ?? 0) >= 3
}

function lonOut(aircraft: Aircraft, point: WbPoint | (AircraftWbLonPoint & AircraftWbLatPoint)) {
  return checkIsOutOfLimits(aircraft.wb!.envelopePoints.map(p => ({ x: p.arm, y: p.mass })), exactArm(point), point.mass)
}

function latOut(aircraft: Aircraft, point: WbPoint | (AircraftWbLonPoint & AircraftWbLatPoint)): boolean {
  if (!hasLatEnvelopePoints(aircraft)) return false
  const latPts = aircraft.wb!.latEnvelopePoints!
  const armBasis = "arm" in latPts[0]
  const out = checkIsOutOfLimits(
    latPts.map(p => ({ x: ("arm" in p) ? p.arm : p.mass, y: p.latArm })),
    armBasis ? exactArm(point) : point.mass,
    exactLatArm(point))
  //the lateral limit is broken whichever way the point left the envelope
  return out.x || out.y
}

// ─── seat assignment ─────────────────────────────────────────────────────────

//Free seat stations in fill order: the copilot seat goes last so passengers fill the cabin first.
function freeSeats(seatStations: AircraftWbStation[], wbLoads: FlightWbLoad[]): AircraftWbStation[] {
  const empty = seatStations.filter(as => wbLoads.findIndex(wbLoad => wbLoad.station.id === as.id) === -1)
  const copilotIndex = empty.findIndex(s => s.id === "copilot");
  if (copilotIndex !== -1) {
    const [copilotObj] = empty.splice(copilotIndex, 1);
    empty.push(copilotObj);
  }
  return empty
}

//Seats crew and passengers. With legIndex, only the passengers on board for that leg are seated
//(see getPassengerStops); without it every passenger is, as for a flight without stops.
export function generateSeatWbLoads(flight: Flight, legIndex?: number): FlightWbLoad[]{
  const acSeatStations = flight.aircraft.wb!.stations.filter(s => s.type === 'seat')
  const wbLoads: FlightWbLoad[] = []
  const crewLoad = (station: AircraftWbStation, crew: Flight["pic"]): FlightWbLoad =>
    ({ station, mass: crew.weight, name: crew.lastName, personId: crew.id, personKind: "crew" })

  if (flight.studentPilot) {
    wbLoads.push(crewLoad(acSeatStations.find(s => s.id === 'pilot')!, flight.studentPilot))
    wbLoads.push(crewLoad(acSeatStations.find(s => s.id === 'copilot')!, flight.pic))
  } else {
    wbLoads.push(crewLoad(acSeatStations.find(s => s.id === 'pilot')!, flight.pic))

    if (flight.sic) {
      wbLoads.push(crewLoad(acSeatStations.find(s => s.id === 'copilot')!, flight.sic))
    }
  }

  if (flight.deadheadCrew){
    var emptyStations = acSeatStations.filter(as => wbLoads.findIndex(wbLoad => wbLoad.station.id === as.id) === -1)
    if (emptyStations.length > 0){
      wbLoads.push(crewLoad(emptyStations[0], flight.deadheadCrew))
    }
  }

  flight.passengers
    .filter(p => legIndex === undefined || isPassengerOnLeg(flight, p.id, legIndex))
    .forEach(p => {
      const seats = freeSeats(acSeatStations, wbLoads)
      if (seats.length === 0){
        throw new Error('No more seats available')
      }
      wbLoads.push({ station: seats[0], mass: p.weight, name: p.lastName, personId: p.id, personKind: "passenger" })
    })

  return wbLoads
}

//Loads for the next leg, carried over from the previous one: people who stay keep their seat,
//passengers whose span ended leave, boarding passengers take free seats, and baggage and seat cargo
//stay aboard (the user adjusts them per leg). Falls back to a fresh seating when the previous leg's
//loads predate person ids.
export function generateLegWbLoads(flight: Flight, legIndex: number, previousLoads: FlightWbLoad[]): FlightWbLoad[] {
  const persons = previousLoads.filter(l => l.station.type === "seat" && l.loadType !== "other")
  if (persons.some(l => l.personId === undefined)) {
    return generateSeatWbLoads(flight, legIndex)
  }
  const acSeatStations = flight.aircraft.wb!.stations.filter(s => s.type === 'seat')
  const loads = previousLoads.filter(l => l.personKind !== "passenger" || isPassengerOnLeg(flight, l.personId, legIndex))
  flight.passengers
    .filter(p => isPassengerOnLeg(flight, p.id, legIndex) && !loads.some(l => l.personId === p.id))
    .forEach(p => {
      const seats = freeSeats(acSeatStations, loads)
      if (seats.length === 0){
        throw new Error('No more seats available')
      }
      loads.push({ station: seats[0], mass: p.weight, name: p.lastName, personId: p.id, personKind: "passenger" })
    })
  return loads
}

// ─── calculation & limits ────────────────────────────────────────────────────

export function getCalculatedFlightWbPoints(aircraft: Aircraft, wbLoads: FlightWbLoad[], fuelOnBoard: FlightFuelOnBoard[], tripNeededFuelMass: number): CalculatedFlightWbPoints {
  const emptyFuel = calculateEmptyFuelMass(aircraft, wbLoads)
  //burnFuel takes off mass only: bring each tank's volume down with it, so the landing fuel reads right in
  //litres/gallons too (the next leg departs with it when nothing is uplifted)
  const landingFuelOnBoard = burnFuel(fuelOnBoard ?? [], tripNeededFuelMass)
    .map((f) => ({ ...f, fuel: Math.round(FuelConverter.toFuelUnit(aircraft, f.mass)) }))
  return {
    takeoff: toPoint(addFuel(emptyFuel, fuelOnBoard)),
    landing: { ...toPoint(addFuel(emptyFuel, landingFuelOnBoard)), landingFuelOnBoard },
    emptyFuel: toPoint(emptyFuel),
  }
}

export type CalculatedFlightWbPoints = {
  takeoff: WbPoint;
  landing: WbPoint & {landingFuelOnBoard: FlightFuelOnBoard[]};
  emptyFuel: WbPoint;
}

//Per-point in/out-of-envelope check for UI (limits box, chart point coloring).
//Reuses the same geometry as calculateOutOfLimitsTexts.
export function checkPointOutOfEnvelope(aircraft: Aircraft, point: WbPoint | (AircraftWbLonPoint & AircraftWbLatPoint)): { mass: boolean; arm: boolean; latArm: boolean } {
  const { x: armOut, y: massOut } = lonOut(aircraft, point)
  return { mass: massOut, arm: armOut, latArm: latOut(aircraft, point) }
}

//Per-station structural limits: each station's own maxMass, plus placard limits that cap a pair
//of stations together (AircraftWbStation.combinedLimitWith) — e.g. a baggage bay under a seat
//where the seat limit covers occupant and bag as one. Over-limit entry is allowed in the UI and
//never silently clamped, so these surface here as hard errors alongside the envelope checks.
export function calculateStationLimitTexts(aircraft: Aircraft, wbLoads: FlightWbLoad[], fuelOnBoard: FlightFuelOnBoard[] = []): string[] {
  const unit = aircraft.massUnit
  const massOf = (stationId: string) => wbLoads.find(l => l.station.id === stationId)?.mass ?? 0

  const texts = wbLoads
    .filter(l => l.station.maxMass > 0 && l.mass > l.station.maxMass)
    .map(l => `${l.station.name} load ${l.mass} ${unit} over max ${l.station.maxMass}`)

  //a tank can't hold more than its capacity (the tank's maxMass), whatever the weight and CG come to.
  //Masses are whole units, so a full tank may round just above a fractional capacity (377 vs 376.7).
  fuelOnBoard
    .filter(f => f.station.maxMass > 0 && f.mass > Math.ceil(f.station.maxMass))
    .forEach(f => {
      const capacity = Math.floor(FuelConverter.toFuelUnit(aircraft, f.station.maxMass))
      texts.push(`${f.station.name} ${Math.round(f.fuel)} ${aircraft.fuelUnit} over the tank's ${capacity} ${aircraft.fuelUnit}`)
    })

  aircraft.wb!.stations.forEach(station => {
    const combined = station.combinedLimitWith
    if (!combined) return
    const other = aircraft.wb!.stations.find(s => s.id === combined.stationId)
    //a profile pointing at a station that isn't there is a data error, not a loading error —
    //ignore it rather than blocking every sheet for the aircraft
    if (!other) return
    const total = massOf(station.id) + massOf(other.id)
    if (total > combined.maxMass) {
      texts.push(`${other.name} + ${station.name} ${total} ${unit} over combined max ${combined.maxMass}`)
    }
  })

  return texts
}

export function calculateOutOfLimitsTexts(calculatedFlightWbPoints: CalculatedFlightWbPoints, aircraft: Aircraft): string[] {
  const { takeoff, landing } = calculatedFlightWbPoints
  const { x: armTakeoffOut, y: massTakeoffOut } = lonOut(aircraft, takeoff)
  const { x: armLandingOut, y: massLandingOut } = lonOut(aircraft, landing)

  //the profile's max mass is a limit of its own — an envelope drawn above it must not pass the sheet
  const towOverMax = takeoff.mass > getWbMaxMass(aircraft.wb!)

  var texts = []
  if (towOverMax) texts.push(`Takeoff Weight ${Math.round(takeoff.mass)} ${aircraft.massUnit} is over max ${getWbMaxMass(aircraft.wb!)}`)
  else if (massTakeoffOut) texts.push('Takeoff Weight is out of limits')
  if (armTakeoffOut) texts.push('Takeoff CG is out of limits')
  if (massLandingOut) texts.push('Landing Weight is out of limits')
  if (armLandingOut) texts.push('Landing CG is out of limits')

  if (latOut(aircraft, takeoff)) texts.push('Takeoff Lat CG is out of limits')
  if (latOut(aircraft, landing)) texts.push('Landing Lat CG is out of limits')

  const landingFuel = landing.landingFuelOnBoard.reduce((acc, f) => acc + f.mass, 0)
  const minFuel = FuelConverter.toMassUnit(aircraft, aircraft.fuelPerHour / 3) // need 20 mins
  if (landingFuel < minFuel) texts.push('Landing fuel is too low')

  return texts
}

export function calculateWarningOutOfLimitsTexts(calculatedFlightWbPoints: CalculatedFlightWbPoints, aircraft: Aircraft): string[] {
  const { x: armEmptyFuelOut, y: massEmptyFuelOut } = lonOut(aircraft, calculatedFlightWbPoints.emptyFuel)

  var texts = []
  if (massEmptyFuelOut) texts.push('Empty Fuel Weight is out of limits')
  if (armEmptyFuelOut) texts.push('Empty Fuel CG is out of limits')
  if (latOut(aircraft, calculatedFlightWbPoints.emptyFuel)) texts.push('Empty Fuel Lat CG is out of limits')

  return texts
}
